/**
 * Low Stock Message Handler
 * Displays "Only X left" message when variant inventory is low
 */

import { ThemeEvents } from '@theme/events';

class LowStockMessage extends HTMLElement {
  /** @type {number} */
  threshold = 0;
  /** @type {string} */
  template = 'Only {{ num }} left';

  connectedCallback() {
    // CRITICAL: Get threshold from closest variant-picker, not document-wide search
    const variantPicker = this.closest('variant-picker');
    const thresholdValue = variantPicker?.dataset.lowStockThreshold;
    this.threshold = thresholdValue ? parseInt(thresholdValue, 10) || 0 : 0;
    this.template = this.dataset.template || 'Only {{ num }} left';
    
    if (this.threshold > 0) {
      this.updateMessage();
      
      // Listen to both section and document-level events (like product-price.js does)
      // This ensures we catch events even if the element gets morphed
      const closestSection = this.closest('.shopify-section, dialog');
      if (closestSection) {
        closestSection.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
      }
      document.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    } else {
      // Threshold is 0 or not set - hide message
      this.style.display = 'none';
    }
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (closestSection) {
      closestSection.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    }
    document.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  /**
   * Handle variant update event
   * @param {CustomEvent} event - The variant update event
   */
  handleVariantUpdate = (event) => {
    // Check if this event is for our product (if productId is provided)
    const eventProductId = event.detail?.data?.productId;
    const variantPicker = this.closest('variant-picker');
    const ourProductId = variantPicker?.dataset.productId;
    
    // If product IDs don't match, ignore this event
    if (eventProductId && ourProductId && eventProductId !== ourProductId) {
      return;
    }
    
    // Use event data first (from new HTML), then fallback to script tag after morph completes
    const eventVariant = event.detail?.resource;
    
    if (eventVariant && typeof eventVariant.inventory_quantity !== 'undefined') {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        this.updateMessage(eventVariant);
      });
    } else {
      // Use requestAnimationFrame to ensure the script tag has been updated by morph()
      requestAnimationFrame(() => {
        this.updateMessage();
      });
    }
  };

  /**
   * Update the low stock message
   * @param {Object} [variantData] - Optional variant data from event
   */
  updateMessage(variantData) {
    if (this.threshold <= 0) {
      this.style.display = 'none';
      return;
    }

    const variantJson = variantData || this.getVariantFromJson();
    
    if (!variantJson) {
      this.style.display = 'none';
      return;
    }

    const inventory = variantJson.inventory_quantity;
    const inventoryManagement = variantJson.inventory_management;

    // Only show if inventory is being tracked
    if (inventoryManagement !== 'shopify') {
      this.style.display = 'none';
      return;
    }

    // Show message if inventory is > 0 and <= threshold
    if (typeof inventory === 'number' && inventory > 0 && inventory <= this.threshold) {
      const template = this.template || 'Only {{ num }} left';
      this.textContent = template.replace('{{ num }}', String(inventory));
      this.style.display = '';
    } else {
      this.style.display = 'none';
    }
  }

  getVariantFromJson() {
    const variantPicker = this.closest('variant-picker');
    if (!variantPicker) return null;

    const scriptTag = variantPicker.querySelector('script[type="application/json"]');
    if (!scriptTag || !scriptTag.textContent) return null;

    try {
      return JSON.parse(scriptTag.textContent);
    } catch (e) {
      console.error('Failed to parse variant JSON:', e);
      return null;
    }
  }
}

if (!customElements.get('low-stock-message')) {
  customElements.define('low-stock-message', LowStockMessage);
}

